import { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";

export default function PolygonTool({
  initialPoints,
  labels: propLabels,
  draggable = true,
  showStats = true
}) {
  // Default vertices
  const defaultPts = [
    { x: 100, y: 80 },
    { x: 200, y: 80 },
    { x: 150, y: 150 }
  ];

  // State for triangle points
  const [points, setPoints] = useState(
    initialPoints
      ? initialPoints.map(p => ({ x: p.x, y: p.y }))
      : defaultPts
  );

  // Sync points when initialPoints prop changes
  useEffect(() => {
    if (initialPoints) {
      setPoints(initialPoints.map(p => ({ x: p.x, y: p.y })));
    }
  }, [initialPoints]);

  // Distance helper
  function distance(p1, p2) {
    return Math.hypot(p2.x - p1.x, p2.y - p1.y);
  }

  // Side lengths & perimeter
  const sideAB = distance(points[0], points[1]);
  const sideBC = distance(points[1], points[2]);
  const sideCA = distance(points[2], points[0]);
  const perimeter = sideAB + sideBC + sideCA;

  // Angles (useMemo + angleAt only inside)
  const angles = useMemo(() => {
    function angleAt(idx) {
      const A = points[idx];
      const B = points[(idx + 1) % 3];
      const C = points[(idx + 2) % 3];
      const v1 = [B.x - A.x, B.y - A.y];
      const v2 = [C.x - A.x, C.y - A.y];
      const dot = v1[0] * v2[0] + v1[1] * v2[1];
      const mag = Math.hypot(...v1) * Math.hypot(...v2);
      if (mag === 0) return 0;
      const cosθ = Math.max(-1, Math.min(1, dot / mag));
      return (Math.acos(cosθ) * 180) / Math.PI;
    }
    return [angleAt(0), angleAt(1), angleAt(2)];
  }, [points]);

  // Labels fallback
  const labels = useMemo(() => propLabels || ["A", "B", "C"], [propLabels]);
  const svgRef = useRef(null);

  // Draw triangle and optionally attach drag
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Triangle polygon
    svg
      .append("polygon")
      .attr("points", points.map(p => `${p.x},${p.y}`).join(" "))
      .attr("fill", "#ffcdd2")
      .attr("stroke", "#c62828")
      .attr("stroke-width", 2);

    // Vertices
    const circles = svg
      .selectAll("circle")
      .data(points)
      .enter()
      .append("circle")
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", 8)
      .attr("fill", "#ef9a9a")
      .attr("stroke", "#b71c1c")
      .attr("stroke-width", 2);

    // Only enable dragging if requested
    if (draggable) {
      const drag = d3.drag().on("drag", (event, d) => {
        d.x = event.x;
        d.y = event.y;
        setPoints([...points]);
      });
      circles.call(drag);
    }

    // Angle labels
    svg
      .selectAll(".vertexLabel")
      .data(points)
      .enter()
      .append("text")
      .attr("class", "vertexLabel")
      .attr("x", d => d.x + 10)
      .attr("y", d => d.y)
      .attr("font-size", 16)
      .attr("fill", "#444")
      .text((d, i) => `${labels[i]} (${angles[i].toFixed(1)}°)`);
  }, [points, draggable, labels, angles]);

  return (
    <div style={{ border: "1px solid #ccc", padding: 10, borderRadius: 6 }}>
      <h4>Polygon (Triangle) Interactive</h4>
      {draggable && (
        <p>Drag the vertices to change the shape. Observe side lengths & angles.</p>
      )}
      <svg
        ref={svgRef}
        width={300}
        height={200}
        style={{ background: "#f9f9f9", display: "block", marginBottom: 10 }}
      />
      {showStats && (
        <>
          <p><strong>Side Lengths</strong> (approx):</p>
          <ul>
            <li>AB: {sideAB.toFixed(1)}</li>
            <li>BC: {sideBC.toFixed(1)}</li>
            <li>CA: {sideCA.toFixed(1)}</li>
          </ul>
          <p>Perimeter: {perimeter.toFixed(1)}</p>
          <p><strong>Angles</strong> (approx):</p>
          <ul>
            <li>Angle A: {angles[0].toFixed(1)}°</li>
            <li>Angle B: {angles[1].toFixed(1)}°</li>
            <li>Angle C: {angles[2].toFixed(1)}°</li>
          </ul>
          <p>Notice how A + B + C ≈ {(angles[0] + angles[1] + angles[2]).toFixed(1)}°, around 180°.</p>
        </>
      )}
    </div>
  );
}
